import { useEffect, useMemo } from "react";
import { useTranslation } from "../../core/translation.core";
import { Cart, CartFormData, CartFormDataItem, DeliveryOption } from "../../types/cart";
import { UseFormReturn, useWatch } from "react-hook-form";
import useSelectShipOption from "../../hooks/services/useSelectShipOption";
import { useCart } from "../../hooks/cart/useCart.hook";
import useGetShipOptions from "../cart/useGetShipOptions.hook";

type ShipOptionWithValidProducts = DeliveryOption & { validForProducts: string[] };

interface DeliveryMethodsSectionProps {
  methods: UseFormReturn<CartFormData>;
}

export default function DeliveryMethodsSection({ methods }: DeliveryMethodsSectionProps) {
  const { t, msg } = useTranslation();
  const { cart, isLoading: cartIsLoading } = useCart();
  const { items: selectedCartItems = [] } = useWatch({ control: methods.control });

  const { data: { deliveryOptions } = {}, isPending: isLoading } = useGetShipOptions(
    selectedCartItems as CartFormDataItem[],
    cartIsLoading
  );

  const { selectedShipOption, handleSelectShipOption, isSessionReady } = useSelectShipOption({
    deliveryOptions, //
    methods,
    items: cart?.items ?? [],
    isTemp: true,
  });

  const isOnlyGiftCard = useMemo(() => {
    if (!cart) return undefined;

    return cart.items.every((i) => i.product?.productType.toLowerCase() === "giftcard");
  }, [cart]);

  const filteredShipOptions = useFilteredShipOptions(deliveryOptions, cart?.items);

  const shippingOptions = filteredShipOptions.filter((opt) => opt.shipType !== "pickup");

  useEffect(() => {
    if (isOnlyGiftCard && isSessionReady) {
      const freeShipping = shippingOptions.find((opt) => opt.fee === 0);
      if (freeShipping && freeShipping.id !== selectedShipOption?.id) {
        handleSelectShipOption(freeShipping.id);
      }
    }
  }, [isOnlyGiftCard, isSessionReady, shippingOptions, handleSelectShipOption, selectedShipOption]);

  if (isLoading) return null;
  //   return (
  //     <div className="py-24">
  //       <LoadingComp />
  //     </div>
  //   );
  // }

  if (!cart) return null;

  if (filteredShipOptions.length < 1) {
    return (
      <div className="w-full flex items-center justify-center p-4 text-center text-red-400">{t(msg.cart.noShippingOption)}</div>
    );
  }

  if (isOnlyGiftCard) {
    return (
      <div className="bg-green-50 border border-green-100 rounded-lg">
        <div className="flex items-start gap-3 p-3">
          <span className="text-green-500 font-medium mt-0.5">✓</span>
          <span className="text-base">
            {cart?.items[0].product.name} - {t(msg.gift.credited)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5">
      <div className="flex items-center gap-1 mb-5">
        <h2 className="text-lg font-medium">{t(msg.menu.deliveryMethod)}</h2>
      </div>

      <div className="space-y-6">
        <PickupOptions
          deliveryOptions={deliveryOptions}
          cart={cart}
          selectedShipOption={selectedShipOption}
          onSelectShipOption={handleSelectShipOption}
          disabled={!isSessionReady}
        />

        <AddressOptions
          deliveryOptions={deliveryOptions}
          cart={cart}
          selectedShipOption={selectedShipOption}
          onSelectShipOption={handleSelectShipOption}
          disabled={!isSessionReady}
        />
      </div>
    </div>
  );
}

function useFilteredShipOptions(
  deliveryOptions: DeliveryOption[] | undefined,
  cartItems: Cart["items"] | undefined
): ShipOptionWithValidProducts[] {
  return useMemo(() => {
    if (!deliveryOptions?.length || !cartItems?.length) return [];

    return deliveryOptions
      .map((p) => ({
        ...p,
        validForProducts: cartItems.filter((i) => i.product.validShipOptionIds.includes(p.id)).map((j) => j.product.name),
      }))
      .filter((i) => i.validForProducts.length === cartItems.length);
  }, [deliveryOptions, cartItems]);
}

interface PickupOptionsProps {
  deliveryOptions: DeliveryOption[] | undefined;
  cart: Cart;
  selectedShipOption: DeliveryOption | undefined;
  onSelectShipOption: (optionId: string) => void;
  disabled?: boolean;
}

function PickupOptions({ deliveryOptions, cart, selectedShipOption, onSelectShipOption, disabled }: PickupOptionsProps) {
  const { t, msg } = useTranslation();
  const filteredShipOptions = useFilteredShipOptions(deliveryOptions, cart.items);
  const pickupOptions = filteredShipOptions.filter((opt) => opt.shipType === "pickup");

  return (
    <RadioGroup
      title={t(msg.ship.pickupLocations)}
      options={pickupOptions}
      cartItemCount={cart.items.length}
      selectedShipOption={selectedShipOption}
      onSelectShipOption={onSelectShipOption}
      disabled={disabled}
    />
  );
}

interface AddressOptionsProps {
  deliveryOptions: DeliveryOption[] | undefined;
  cart: Cart;
  selectedShipOption: DeliveryOption | undefined;
  onSelectShipOption: (optionId: string) => void;
  disabled?: boolean;
}

function AddressOptions({
  deliveryOptions,
  cart,
  selectedShipOption,
  onSelectShipOption,
  disabled,
}: AddressOptionsProps) {
  const { t, msg } = useTranslation();
  const filteredShipOptions = useFilteredShipOptions(deliveryOptions, cart.items);
  const shippingOptions = filteredShipOptions.filter((opt) => opt.shipType !== "pickup");

  return (
    <RadioGroup
      title={t(msg.ship.shippingAddresses)}
      options={shippingOptions}
      cartItemCount={cart.items.length}
      selectedShipOption={selectedShipOption}
      onSelectShipOption={onSelectShipOption}
      disabled={disabled}
    />
  );
}

interface RadioGroupProps {
  title: string;
  options: ShipOptionWithValidProducts[];
  cartItemCount: number;
  selectedShipOption: DeliveryOption | undefined;
  onSelectShipOption: (optionId: string) => void;
  disabled?: boolean;
}

function RadioGroup({ title, options, cartItemCount, selectedShipOption, onSelectShipOption, disabled }: RadioGroupProps) {
  const { t, msg } = useTranslation();

  if (options.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium text-gray-700">{title}</h3>
      {options.map((option) => (
        <label
          key={option.id}
          className={`flex items-center ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
        >
          <input
            type="radio"
            name="delivery"
            checked={option.id === selectedShipOption?.id}
            disabled={disabled}
            className="w-4 h-4 accent-green-600 border-gray-300 cursor-pointer mr-3"
            onChange={() => {
              if (!disabled) {
                onSelectShipOption(option.id);
              }
            }}
          />
          <div className="w-full grid grid-cols-2">
            <span className="text-base flex-1">{option.label}</span>
            {option.fee !== undefined && (
              <span className="text-sm text-gray-500 ml-2 text-end">
                {option.fee === 0 ? t(msg.cart.free) : `HK$${option.fee.toFixed(2)}`}
              </span>
            )}
            <span className="col-span-2 text-[11px]">
              {option.validForProducts.length === cartItemCount
                ? "All Products Valid"
                : `${option.validForProducts.length} Products Valid`}
            </span>
          </div>
        </label>
      ))}
    </div>
  );
}
